import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, tap } from 'rxjs';
import { IMEI_STEP_SERVICE, IImeiStepService } from '@app/shared/tokens/imei-step.token';
import { ModalQrScannerComponent } from '@app/shared/components/modal-qr-scanner/modal-qr-scanner.component';
import { Store } from '@ngxs/store';
import { ScreeningState } from '@app/store/screening/screening.state';
import { CdkStepper } from '@angular/cdk/stepper';

@Component({
  selector: 'app-imei-step',
  templateUrl: './imei-step.component.html',
  styleUrls: ['./imei-step.component.sass'],
})
export class ImeiStepComponent implements OnInit {
  idTrade = '';
  isValidated = false;
  imeiControl = new FormControl('', [Validators.required]);

  private _nextRoute: any[];
  private _cancelRoute: any[];
  private _isValidating = false;

  get hasCancelRoute(): boolean {
    return !!this._cancelRoute && this._cancelRoute.length > 0;
  }

  constructor(
    @Inject(IMEI_STEP_SERVICE) private _service: IImeiStepService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _dialog: MatDialog,
    private _toastr: ToastrService,
    private _store: Store,
    @Optional() private _stepper: CdkStepper
  ) { }

  ngOnInit(): void {
    this.idTrade = this._route.parent?.snapshot.paramMap.get('idTrade') ?? '';

    if (!this.idTrade) {
      try {
        const screening = this._store.selectSnapshot(ScreeningState);
        this.idTrade = screening?.idTrade ?? '';
      } catch (e) {
        console.warn('ScreeningState not available or initialized');
      }
    }

    const data = this._route.snapshot.data || {};
    this._nextRoute = data['nextRoute'];
    this._cancelRoute = data['cancelRoute'];

    this.imeiControl.valueChanges.subscribe((value) => {
      this.isValidated = false;
      const cleanVal = (value || '').trim().replace(/\D/g, '');
      if (cleanVal.length === 15) {
        this.imeiControl.setValue(cleanVal, { emitEvent: false });
        this.validate();
      }
    });
  }

  readBarcode(): void {
    const ref = this._dialog.open(ModalQrScannerComponent, { width: '100%', maxWidth: '650px' });
    ref.afterClosed().subscribe((value) => {
      if (value) {
        const cleanVal = value.trim().replace(/\D/g, '');
        this.imeiControl.setValue(cleanVal);
      }
    });
  }

  validate(): void {
    const imei = this.imeiControl.value?.trim();
    if (!imei || this._isValidating) return;

    this._isValidating = true;
    this._service
      .validateImei(imei, this.idTrade)
      .pipe(
        tap(() => {
          this.isValidated = true;
          this._isValidating = false;
        }),
        catchError((error) => {
          const message = error?.errors?.[0] || error?.message || 'IMEI inválido ou não encontrado';
          this.imeiControl.setErrors({ serverError: message });
          this._toastr.error(message);
          this.isValidated = false;
          this._isValidating = false;
          return EMPTY;
        })
      )
      .subscribe();
  }

  goNext(): void {
    const imei = this.imeiControl.value?.trim();
    this._service
      .confirmImei(imei, this.idTrade)
      .pipe(
        tap(() => {
          if (this._stepper) {
            this._stepper.next();
          } else if (this._nextRoute) {
            this._router.navigate(this._nextRoute, { relativeTo: this._route });
          }
        }),
        catchError(() => {
          this._toastr.error('Erro ao confirmar IMEI');
          return EMPTY;
        })
      )
      .subscribe();
  }

  cancel(): void {
    if (this._cancelRoute) {
      this._router.navigate(this._cancelRoute);
    }
  }
}
